import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupCount, ToggleGroupItem } from "@/components/ui/toggle-group";
import { segmentedCount } from "@/components/ui/segmented";
import {
  CeldaPrincipal,
  DataTable,
  Icon,
  KpiCard,
  KpiGrid,
  ProgressBar,
  StatusBadge,
  ToolbarSpacer,
  type EstadoDS,
} from "@/components/ds";
import { Progress } from "@/components/ui/progress";
import { AlertCircle, Ban, FileText, GraduationCap, Loader2 } from "@/components/ds/icons";
import { toast } from "sonner";
import { CapacitacionesDocumentoAcciones } from "@/components/capacitaciones-documentos/CapacitacionesDocumentoAcciones";
import { CeldaVigencia, Tip, VigenciaModal } from "@/components/ds";
import { ApiError } from "@/lib/api";
import { apiCapacitacionesDocumentosAdapter } from "@/lib/capacitaciones-documentos-adapter";
import { cn } from "@/lib/utils";
import { estadoDocumentoDs, resumirDocumentosEmpresa } from "@/lib/documentos-empresa-resumen";
import type { CapacitacionesDocumentoSlot } from "@/types/capacitaciones-documento";

// Único punto donde se elige el adaptador — mismo patrón que
// `DocumentosAdicionalesPanel` (persona-documentos-adapter).
const adapter = apiCapacitacionesDocumentosAdapter;

function mensajeDeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.message) return err.message;
  return fallback;
}

function mensajeDeCarga(err: unknown): string {
  return mensajeDeError(err, "No se pudo cargar el catálogo de documentos institucionales.");
}

function esCancelacion(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatFecha(iso: string): string {
  return new Date(iso).toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Estado canónico del DS para la columna ESTADO. La derivación vive en
 * `documentos-empresa-resumen` porque el Dashboard pliega los mismos slots;
 * acá sólo se le suma el caso de subida en curso, que es estado de esta
 * pantalla y no del dato ("Subiendo…" sobre el tono neutro de "pendiente"). */
function estadoDs(slot: CapacitacionesDocumentoSlot, uploading: boolean): EstadoDS {
  if (uploading) return "pendiente";
  return estadoDocumentoDs(slot);
}

/** Segmented del toolbar — subconjunto de `EstadoDS` que aplica a este
 * catálogo, más el sentinela "todos". */
type FiltroEstado = "todos" | "pendiente" | "subido" | "por_vencer" | "vencido" | "no_aplica";

/** Contador monoespaciado que acompaña cada opción del segmented — mismo
 * patrón que el pie de `DataTable` (`fc-num`), a menor tamaño. */
/** Contador del botón "Sólo fuentes de capacitación", que está fuera del
 *  segmented pero en la misma barra: comparte su clase para que los dos números
 *  de la fila se vean iguales. Dentro del segmented va `<ToggleGroupCount>`. */
function Cuenta({ n }: { n: number }) {
  return <span className={segmentedCount}>{n}</span>;
}

export function CapacitacionesDocumentosPanel() {
  const [slots, setSlots] = useState<CapacitacionesDocumentoSlot[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyTipoId, setBusyTipoId] = useState<number | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<CapacitacionesDocumentoSlot | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Vigencia: modal reusado — "renovar sin recargar" (spec `empresa-documento-vigencia`).
  const [vencimientoTarget, setVencimientoTarget] = useState<CapacitacionesDocumentoSlot | null>(null);

  // Subida inline: `Subir`/`Reemplazar` disparan el picker del SO directo
  // (sin modal) — este estado alimenta la barra de progreso + Cancelar de
  // `CapacitacionesDocumentoAcciones` mientras la subida está en curso. Se
  // indexa por `tipoDocumentoId` porque puede haber a lo más una subida
  // por slot.
  const [uploadingPercents, setUploadingPercents] = useState<Record<number, number>>({});
  const abortControllersRef = useRef<Map<number, AbortController>>(new Map());

  // Toolbar (slice A4): buscador + segmented de estado + toggle "Sólo
  // fuentes de capacitación". Los tres se cruzan (AND) sobre `slots`.
  const [q, setQ] = useState("");
  const [filtro, setFiltro] = useState<FiltroEstado>("todos");
  const [soloCap, setSoloCap] = useState(false);

  const cargar = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      setSlots(await adapter.listSlots());
    } catch (err) {
      setLoadError(mensajeDeCarga(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Al desmontar abortamos cualquier subida en curso: evita subidas colgadas
  // y setState sobre un componente desmontado. `abortControllersRef` es un
  // ref, así que el cleanup no necesita deps.
  useEffect(
    () => () => {
      abortControllersRef.current.forEach((controller) => controller.abort());
    },
    [],
  );

  function actualizarSlot(tipoId: number, next: Partial<CapacitacionesDocumentoSlot>) {
    setSlots((prev) => prev.map((s) => (s.tipo_documento_id === tipoId ? { ...s, ...next } : s)));
  }

  async function handlePickFile(slot: CapacitacionesDocumentoSlot, file: File) {
    const tipoId = slot.tipo_documento_id;
    const controller = new AbortController();
    abortControllersRef.current.set(tipoId, controller);
    setUploadingPercents((prev) => ({ ...prev, [tipoId]: 0 }));
    try {
      const documento = await adapter.upload(slot.slug, file, {
        onProgress: (percent) => setUploadingPercents((prev) => ({ ...prev, [tipoId]: percent })),
        signal: controller.signal,
      });
      actualizarSlot(tipoId, { documento });
      toast.success(`${slot.label} subido correctamente.`);
    } catch (err) {
      // Cancelación del usuario (botón "Cancelar") — no es un error, no se notifica.
      if (!esCancelacion(err)) {
        toast.error(mensajeDeError(err, "No se pudo subir el documento."));
      }
    } finally {
      abortControllersRef.current.delete(tipoId);
      setUploadingPercents((prev) => {
        const next = { ...prev };
        delete next[tipoId];
        return next;
      });
    }
  }

  function handleCancelUpload(tipoId: number) {
    abortControllersRef.current.get(tipoId)?.abort();
  }

  async function handleConfirmDelete() {
    if (!deleteTarget?.documento) return;
    const slot = deleteTarget;
    setIsDeleting(true);
    try {
      await adapter.destroy(slot.slug);
      actualizarSlot(slot.tipo_documento_id, { documento: null });
      toast.success(`${slot.label} eliminado.`);
      setDeleteTarget(null);
    } catch {
      toast.error("No se pudo eliminar el documento.");
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleDownload(slot: CapacitacionesDocumentoSlot) {
    if (!slot.documento) return;
    setBusyTipoId(slot.tipo_documento_id);
    try {
      await adapter.download(slot.slug, slot.documento);
    } catch {
      toast.error("No se pudo descargar el documento.");
    } finally {
      setBusyTipoId(null);
    }
  }

  // Alterna el flag "usar para capacitaciones". Optimista: refleja el cambio
  // en el acto (así el botón Capacitaciones IA se habilita/deshabilita sin
  // latencia) y revierte si el backend falla.
  async function handleToggleUso(slot: CapacitacionesDocumentoSlot, value: boolean) {
    if (!slot.documento) return;
    const documentoPrevio = slot.documento;
    setBusyTipoId(slot.tipo_documento_id);
    actualizarSlot(slot.tipo_documento_id, { documento: { ...documentoPrevio, usar_para_capacitaciones: value } });
    try {
      const documento = await adapter.setUsarParaCapacitaciones(slot.slug, value);
      actualizarSlot(slot.tipo_documento_id, { documento });
    } catch (err) {
      actualizarSlot(slot.tipo_documento_id, { documento: documentoPrevio });
      toast.error(mensajeDeError(err, "No se pudo actualizar el documento."));
    } finally {
      setBusyTipoId(null);
    }
  }

  // Marca/revierte "No aplica" (slice A3). Sin optimismo: el backend puede
  // rechazar con 422 (slot con archivo, o carrera con una subida en curso) y
  // preferimos esperar la respuesta antes de mover el badge/las acciones.
  async function handleToggleNoAplica(slot: CapacitacionesDocumentoSlot) {
    setBusyTipoId(slot.tipo_documento_id);
    try {
      const resultado = await adapter.setNoAplica(slot.slug, !slot.no_aplica);
      actualizarSlot(slot.tipo_documento_id, {
        no_aplica: resultado.no_aplica,
        no_aplica_at: resultado.no_aplica_at,
        no_aplica_por: resultado.no_aplica_por,
      });
      toast.success(
        resultado.no_aplica
          ? `${slot.label} marcado como «No aplica»: deja de contar en el avance.`
          : `${slot.label} vuelve a contar en el avance.`,
      );
    } catch (err) {
      toast.error(mensajeDeError(err, "No se pudo actualizar la marca «No aplica»."));
    } finally {
      setBusyTipoId(null);
    }
  }

  async function handleSubmitVencimiento(opts: Parameters<typeof adapter.updateVencimiento>[1]) {
    if (!vencimientoTarget) return;
    const documento = await adapter.updateVencimiento(vencimientoTarget.slug, opts);
    actualizarSlot(vencimientoTarget.tipo_documento_id, { documento });
  }

  // Tarjetas resumen + contadores del segmented — IT2: los contadores
  // EXCLUYEN "No aplica" (`aplicables`), y "Fuentes de capacitación" se
  // pliega de `usar_para_capacitaciones`, no de un catálogo aparte por tipo.
  // El pliegue es compartido con el Dashboard (regla #1, "un dato, un número").
  const resumen = useMemo(() => resumirDocumentosEmpresa(slots), [slots]);

  const filas = useMemo(() => {
    const query = q.trim().toLowerCase();
    return slots
      .filter((slot) => {
        if (query && !slot.label.toLowerCase().includes(query)) return false;
        if (soloCap && !slot.documento?.usar_para_capacitaciones) return false;
        if (filtro === "todos") return true;
        if (filtro === "no_aplica") return slot.no_aplica;
        return !slot.no_aplica && estadoDs(slot, slot.tipo_documento_id in uploadingPercents) === filtro;
      })
      .map((slot) => ({
        slot,
        uploading: slot.tipo_documento_id in uploadingPercents,
      }));
  }, [slots, uploadingPercents, q, soloCap, filtro]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
          <AlertCircle className="h-6 w-6 text-destructive" />
          <p className="text-sm text-muted-foreground">{loadError}</p>
          <Button variant="outline" size="sm" onClick={cargar}>
            Reintentar
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (slots.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <p className="text-sm text-muted-foreground">
            No hay tipos de documento configurados todavía.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Tarjetas resumen — la primera (progreso) es una composición manual
          porque combina una `ProgressBar` con un pie de dos partes; las
          otras cuatro son `KpiCard` clicables que alimentan el segmented de
          abajo (filtros cruzados, criterio de A4). */}
      <KpiGrid>
        <div className="bg-card flex flex-col gap-2 rounded-xl border px-4 py-3.5">
          <span className="text-muted-foreground text-xs">Documentos que te aplican</span>
          <ProgressBar valor={resumen.cargados} total={resumen.aplicables} alto={7} />
          <div className="text-soft flex items-center gap-1.5 text-[11px]">
            <span>{resumen.faltantes} sin subir</span>
            <span>·</span>
            <button
              type="button"
              onClick={() => setFiltro("no_aplica")}
              className="hover:text-foreground underline decoration-dotted underline-offset-2"
            >
              {resumen.nNA} marcados como no aplica
            </button>
          </div>
        </div>
        <KpiCard
          label="Pendientes"
          valor={resumen.pendientes}
          detalle={`de ${resumen.aplicables} documentos que te aplican`}
          onClick={() => setFiltro("pendiente")}
        />
        <KpiCard
          label="Por vencer"
          valor={resumen.porVencer}
          detalle="vencen en 30 días o menos"
          onClick={() => setFiltro("por_vencer")}
        />
        <KpiCard
          label="Vencidos"
          valor={resumen.vencidos}
          detalle="necesitan renovar la vigencia"
          alerta={resumen.vencidos > 0}
          onClick={() => setFiltro("vencido")}
        />
        <KpiCard
          label="Fuentes de capacitación"
          valor={resumen.nCapActivas}
          detalle="documentos habilitados para generar capacitaciones IA"
          onClick={() => {
            setSoloCap(true);
            setFiltro("todos");
          }}
        />
      </KpiGrid>

      {/* data-tour="capacitaciones-lista": ancla de la guía guiada. */}
      <Card data-tour="capacitaciones-lista">
        <CardContent className="pt-6">
          <DataTable
            rows={filas}
            getRowKey={(fila) => fila.slot.tipo_documento_id}
            minWidth={908}
            pageSize={false}
            emptyMessage="Ningún documento coincide con el filtro."
            emptyIcon="search_off"
            toolbar={
              <>
                <div className="relative w-[240px]">
                  <Icon
                    name="search"
                    size={16}
                    className="text-icon-soft absolute top-1/2 left-2.5 -translate-y-1/2"
                  />
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Buscar documento…"
                    className="h-8 pl-8 text-xs"
                  />
                </div>
                <ToggleGroup
                  type="single"
                  variant="segmented"
                  value={filtro}
                  onValueChange={(v) => v && setFiltro(v as FiltroEstado)}
                >
                  <ToggleGroupItem value="todos">
                    Todos
                    <ToggleGroupCount>{slots.length}</ToggleGroupCount>
                  </ToggleGroupItem>
                  <ToggleGroupItem value="pendiente">
                    Pendientes
                    <ToggleGroupCount>{resumen.pendientes}</ToggleGroupCount>
                  </ToggleGroupItem>
                  <ToggleGroupItem value="subido">
                    Subidos
                    <ToggleGroupCount>{resumen.subidos}</ToggleGroupCount>
                  </ToggleGroupItem>
                  <ToggleGroupItem value="por_vencer">
                    Por vencer
                    <ToggleGroupCount>{resumen.porVencer}</ToggleGroupCount>
                  </ToggleGroupItem>
                  <ToggleGroupItem value="vencido">
                    Vencidos
                    <ToggleGroupCount>{resumen.vencidos}</ToggleGroupCount>
                  </ToggleGroupItem>
                  <ToggleGroupItem value="no_aplica">
                    No aplica
                    <ToggleGroupCount>{resumen.nNA}</ToggleGroupCount>
                  </ToggleGroupItem>
                </ToggleGroup>
                <ToolbarSpacer />
                <button
                  type="button"
                  onClick={() => setSoloCap((v) => !v)}
                  className={cn(
                    "flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors",
                    soloCap
                      ? "border-primary bg-primary-50 text-primary-strong"
                      : "border-input text-muted-foreground hover:bg-muted",
                  )}
                >
                  <GraduationCap className="h-3.5 w-3.5" />
                  Sólo fuentes de capacitación
                  <Cuenta n={resumen.nCapActivas} />
                </button>
              </>
            }
            columns={[
            {
              key: "documento",
              header: "Documento",
              width: "minmax(280px,1fr)",
              cell: ({ slot }) => {
                const cap = !!slot.documento?.usar_para_capacitaciones;
                const Icono = slot.no_aplica ? Ban : cap ? GraduationCap : FileText;
                return (
                  <CeldaPrincipal
                    titulo={
                      <span className="flex items-center gap-2">
                        <Icono
                          className={cn("size-[18px] shrink-0", cap ? "text-primary" : "text-icon-soft")}
                        />
                        {slot.label}
                      </span>
                    }
                  />
                );
              },
            },
            {
              key: "estado",
              header: "Estado",
              width: "126px",
              cell: ({ slot, uploading }) => (
                <StatusBadge estado={estadoDs(slot, uploading)}>
                  {uploading ? "Subiendo…" : undefined}
                </StatusBadge>
              ),
            },
            {
              key: "archivo",
              header: "Archivo",
              width: "minmax(190px,220px)",
              cell: ({ slot, uploading }) => {
                if (uploading) {
                  return (
                    <div className="max-w-[200px] pr-3">
                      <Progress value={uploadingPercents[slot.tipo_documento_id] ?? 0} className="h-1.5" />
                    </div>
                  );
                }
                if (!slot.documento) {
                  return <span className="text-icon-soft text-xs">—</span>;
                }
                const doc = slot.documento;
                return (
                  <div className="flex min-w-0 flex-col gap-0.5 pr-3">
                    <span className="fc-truncate font-mono text-[11.5px]">{doc.filename_original}</span>
                    <span className="text-soft text-[10.5px]">
                      {formatBytes(doc.size_bytes)} · {formatFecha(doc.uploaded_at)}
                    </span>
                  </div>
                );
              },
            },
            {
              key: "vigencia",
              header: "Vigencia",
              // 175 y no 150: la celda pasó de una línea a las dos del DS, y
              // "en 224 días · 2 avisos" no entra en 150px sin recortarse.
              width: "175px",
              cell: ({ slot }) => {
                // Sin archivo la celda queda muda a propósito: acá el
                // vencimiento no se puede configurar antes de subir (el modal
                // se abre sólo con `slot.documento`), así que "al subir se
                // calcula" prometería algo que esta pantalla no ofrece.
                if (!slot.documento && !slot.no_aplica) {
                  return <span className="text-icon-soft text-xs">—</span>;
                }
                const doc = slot.documento;
                return (
                  <CeldaVigencia
                    label={slot.label}
                    noExigible={slot.no_aplica}
                    fechaVencimiento={doc?.fecha_vencimiento ?? null}
                    avisos={doc?.notificar_dias?.length ?? 0}
                    estado={doc?.estado_vencimiento}
                    onEditar={() => setVencimientoTarget(slot)}
                  />
                );
              },
            },
            {
              key: "fuente",
              header: "Fuente de capacitación",
              width: "168px",
              // `index === 0`: ancla de la guía (`capacitaciones-fuente`) SOLO
              // en la primera fila, mismo criterio que `tourAnchors` en la
              // columna de acciones — si no, habría ~25 anclas iguales.
              cell: ({ slot }, index) => {
                if (slot.no_aplica) return <span className="text-icon-soft text-xs">—</span>;
                const doc = slot.documento;
                return (
                  <Tip texto={doc ? "Usar este documento como fuente de capacitación" : "Sube el archivo primero"}>
                    <label
                      data-tour={index === 0 ? "capacitaciones-fuente" : undefined}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground"
                    >
                      <Switch
                        checked={!!doc?.usar_para_capacitaciones}
                        onCheckedChange={(value) => handleToggleUso(slot, value)}
                        disabled={busyTipoId === slot.tipo_documento_id || !doc}
                        aria-label={`Usar ${slot.label} para capacitaciones`}
                      />
                      <span className="hidden lg:inline">
                        {!doc ? "Sube el archivo" : doc.usar_para_capacitaciones ? "Habilitado" : "Desactivado"}
                      </span>
                    </label>
                  </Tip>
                );
              },
            },
            {
              key: "acciones",
              header: "Acciones",
              // 148px: la variante más ancha NO es la de 3 íconos (92px) sino
              // la de subida en curso — "100%" + botón Cancelar, ~137px medidos.
              width: "148px",
              align: "right",
              srOnlyHeader: true,
              cell: ({ slot, uploading }, index) => (
                <CapacitacionesDocumentoAcciones
                  slot={slot}
                  busy={busyTipoId === slot.tipo_documento_id}
                  uploading={uploading}
                  uploadPercent={uploadingPercents[slot.tipo_documento_id] ?? 0}
                  tourAnchors={index === 0}
                  onPickFile={(file) => handlePickFile(slot, file)}
                  onCancelUpload={() => handleCancelUpload(slot.tipo_documento_id)}
                  onDownload={() => handleDownload(slot)}
                  onRequestDelete={() => setDeleteTarget(slot)}
                  onToggleNoAplica={() => handleToggleNoAplica(slot)}
                />
              ),
            },
            ]}
          />
        </CardContent>
      </Card>

      {/* Pie — pliega SIEMPRE del mismo conjunto que la tabla (IT2), no de un
          contador aparte. */}
      <div className="text-muted-foreground flex flex-wrap items-center justify-between gap-2 text-xs">
        <span>
          Mostrando <span className="fc-num">{filas.length}</span> de{" "}
          <span className="fc-num">{slots.length}</span> · <span className="fc-num">{resumen.aplicables}</span>{" "}
          te aplican, <span className="fc-num">{resumen.nNA}</span> no
        </span>
        <span>
          <span className="fc-num">{resumen.nCapActivas}</span> marcados como fuente de capacitación
        </span>
      </div>

      <VigenciaModal
        open={vencimientoTarget !== null}
        onOpenChange={(open) => !open && setVencimientoTarget(null)}
        label={vencimientoTarget?.label ?? ""}
        documento={vencimientoTarget?.documento ?? null}
        onSubmit={handleSubmitVencimiento}
      />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar documento</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.documento ? (
                <>
                  Se eliminará <strong>{deleteTarget.documento.filename_original}</strong> de{" "}
                  <strong>{deleteTarget.label}</strong>. Esta acción no se puede deshacer.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDelete();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
